<script setup lang="ts">
import { computed, ref, watch, type ComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import type { UseQueryReturnType } from '@tanstack/vue-query';
import type { ConversationsQueryParams } from '@/common/api/conversations.api';
import ConversationItem from './ConversationItem.vue';
import ConversationsPagination from './ConversationsPagination.vue';
import LoadingState from '@/components/loading/LoadingState.vue';
import ErrorState from '@/components/loading/ErrorState.vue';
import EmptyState from '@/components/loading/EmptyState.vue';

interface Props {
  title: string;
  requesterId: number | string;
  queryHook: (params: ComputedRef<ConversationsQueryParams | null>) => UseQueryReturnType<any, Error>;
  customisation: {
    isVisible: ComputedRef<boolean>;
    labelColor?: ComputedRef<string | undefined>;
    textColor: ComputedRef<string | undefined>;
    subTextColor: ComputedRef<string | undefined>;
    backgroundColor: ComputedRef<string | undefined>;
    backgroundColorTagResolved?: ComputedRef<string | undefined>;
    backgroundColorTagClosed?: ComputedRef<string | undefined>;
    textColorTagResolved?: ComputedRef<string | undefined>;
    textColorTagClosed?: ComputedRef<string | undefined>;
    borderColor: ComputedRef<string | null>;
  };
  noTag?: boolean;
  limit?: number;
  /** When set, page is kept in route query (replace) so back from conversation restores the list page; pagination does not push history entries. */
  pageQueryKey?: string;
  /** When set, highlights the matching ticket as recently resolved (same rules as My Request). */
  recentResolvedTicketId?: number | string | null;
}

const props = withDefaults(defineProps<Props>(), {
  limit: 5,
  pageQueryKey: undefined,
});

const route = useRoute();
const router = useRouter();

const currentPage = ref(1);

const getSingleQueryValue = (value: string | (string | null)[] | null | undefined): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value[0] ?? undefined;
  return value;
};

const parsePageFromRouteQuery = (): number => {
  if (!props.pageQueryKey) return 1;
  const raw = getSingleQueryValue(route.query[props.pageQueryKey] as string | string[] | undefined);
  const n = parseInt(String(raw ?? '1'), 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return n;
};

watch(
  () => [route.name, route.query[props.pageQueryKey ?? '']] as const,
  () => {
    if (!props.pageQueryKey || route.name !== 'conversations') return;
    currentPage.value = parsePageFromRouteQuery();
  },
  { immediate: true }
);

const queryParams = computed(() => {
  return {
    requesterId: props.requesterId,
    page: currentPage.value,
    limit: props.limit,
  };
});

const queryResult = props.queryHook(queryParams);

const tickets = computed(() => {
  if (!queryResult.data.value?.data) return [];
  return queryResult.data.value.data;
});

const totalTickets = computed(() => {
  return queryResult.data.value?.total || 0;
});

const isLoadingConversations = computed(() => queryResult.isPending.value);
const errorConversations = computed(() => queryResult.error.value);

const {
  isVisible,
  labelColor,
  textColor,
  subTextColor,
  backgroundColor,
  borderColor,
  backgroundColorTagResolved,
  backgroundColorTagClosed,
  textColorTagResolved,
  textColorTagClosed,
} = props.customisation;

const sectionTitleStyle = computed(() => ({
  color: labelColor?.value ?? undefined,
}));

const handlePageChange = (page: number) => {
  if (!props.pageQueryKey) {
    currentPage.value = page;
    return;
  }

  const nextQuery = { ...route.query } as Record<string, string | string[] | undefined>;
  if (page <= 1) {
    delete nextQuery[props.pageQueryKey];
  } else {
    nextQuery[props.pageQueryKey] = String(page);
  }

  router.replace({ name: 'conversations', query: nextQuery });
};

const isRecentResolvedTicket = (ticketId: number | string) => {
  const markId = props.recentResolvedTicketId;
  if (markId === null || markId === undefined) return false;
  return String(ticketId) === String(markId);
};
</script>

<template>
  <div v-if="isVisible" class="conversations-section">
    <span class="section-title theme-font-h4" :style="sectionTitleStyle">{{ title }} ({{ totalTickets }})</span>

    <LoadingState v-if="isLoadingConversations" />
    <ErrorState v-else-if="errorConversations" message="Failed to load conversations" />

    <template v-else>
      <EmptyState v-if="tickets.length === 0" :message="`No ${title.toLowerCase()}`" />

      <div v-else class="conversations-list">
        <ConversationItem
          v-for="ticket in tickets"
          :key="ticket.id"
          :ticket="ticket"
          :text-color="textColor"
          :sub-text-color="subTextColor"
          :background-color="backgroundColor"
          :border-color="borderColor"
          :background-color-tag-resolved="backgroundColorTagResolved"
          :background-color-tag-closed="backgroundColorTagClosed"
          :text-color-tag-resolved="textColorTagResolved"
          :text-color-tag-closed="textColorTagClosed"
          :no-tag="noTag"
          :show-recent-resolved-indicator="isRecentResolvedTicket(ticket.id)"
        />
      </div>

      <ConversationsPagination
        :total="totalTickets"
        :current="currentPage"
        :limit="limit"
        @change-page="handlePageChange"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.conversations-section {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.conversations-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
